import React, { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import {
    Box, Typography, TextField, Button, CircularProgress,
    Table, TableBody, TableCell, TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { notify } from '../notifications';

interface TableInfo {
    id: number;
    cName: string;
    cType: string;
    displayName: string;
    InputBlock: number;
    formula: string;
    sortByGroup: string;
    important: number;
}

interface GroupedFields {
    [key: string]: TableInfo[];
}

interface AppState {
    loading: boolean;
    isInitialized: boolean;
    isLocked: boolean;
    serverDate: string;
    serverDateISO: string;
    pharmacyNum: string;
    lockTimer: string;
    currentServerTime: Date | null;
    pastValue: string;
}

type AppAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_INITIALIZED'; payload: boolean }
    | { type: 'SET_LOCKED'; payload: boolean }
    | { type: 'SET_SERVER_DATE'; payload: { date: string; dateISO: string } }
    | { type: 'SET_PHARMACY_NUM'; payload: string }
    | { type: 'SET_LOCK_TIMER'; payload: string }
    | { type: 'SET_SERVER_TIME'; payload: Date }
    | { type: 'SET_PAST_VALUE'; payload: string }
    | { type: 'INIT_DATA'; payload: Partial<AppState> };

const appReducer = (state: AppState, action: AppAction): AppState => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_INITIALIZED':
            return { ...state, isInitialized: action.payload };
        case 'SET_LOCKED':
            return { ...state, isLocked: action.payload };
        case 'SET_SERVER_DATE':
            return { ...state, serverDate: action.payload.date, serverDateISO: action.payload.dateISO };
        case 'SET_PHARMACY_NUM':
            return { ...state, pharmacyNum: action.payload };
        case 'SET_LOCK_TIMER':
            return { ...state, lockTimer: action.payload };
        case 'SET_SERVER_TIME':
            return { ...state, currentServerTime: action.payload };
        case 'SET_PAST_VALUE':
            return { ...state, pastValue: action.payload };
        case 'INIT_DATA':
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

const initialAppState: AppState = {
    loading: true,
    isInitialized: false,
    isLocked: false,
    serverDate: '',
    serverDateISO: '',
    pharmacyNum: '',
    lockTimer: '',
    currentServerTime: null,
    pastValue: '',
};

// LocalStorage кэш для структуры таблицы (24 часа)
const TABLE_FIELDS_CACHE_KEY = 'dashboard_table_fields';
const TABLE_FIELDS_CACHE_TTL = 24 * 60 * 60 * 1000;

const getCachedTableFields = (): TableInfo[] | null => {
    try {
        const cached = localStorage.getItem(TABLE_FIELDS_CACHE_KEY);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < TABLE_FIELDS_CACHE_TTL) {
                return data;
            }
        }
    } catch { /* ignore */ }
    return null;
};

const setCachedTableFields = (data: TableInfo[]) => {
    try {
        localStorage.setItem(TABLE_FIELDS_CACHE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
        }));
    } catch { /* ignore */ }
};

const CurrentDashboard: React.FC = () => {
    const [state, dispatch] = useReducer(appReducer, initialAppState);
    const [tableInfo, setTableInfo] = useState<TableInfo[]>([]);
    const [formValues, setFormValues] = useState<{ [key: string]: string }>({});
    const [isFormValid, setIsFormValid] = useState(false);
    const navigate = useNavigate();

    // Группировка полей
    const groupedFields = useMemo(() => {
        return tableInfo.reduce((acc: GroupedFields, field) => {
            const group = field.sortByGroup || 'Прочее';
            if (!acc[group]) acc[group] = [];
            acc[group].push(field);
            return acc;
        }, {});
    }, [tableInfo]);

    useEffect(() => {
        document.title = 'Таблица выручки | Zerde';
    }, []);

    // Проверка даты каждую минуту
    useEffect(() => {
        const checkDate = async () => {
            try {
                const response = await axios.get('https://tbl.zerdegroup.kz/api/current-date');
                if (response.data.date !== state.serverDateISO) {
                    navigate('/');
                }
            } catch { /* ignore */ }
        };
        const timer = setInterval(checkDate, 60000);
        return () => clearInterval(timer);
    }, [state.serverDateISO, navigate]);

    const formatTime = useCallback((h: number, m: number, s: number): string => {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }, []);

    const updateLockTimer = useCallback((currentTime: Date) => {
        const hours = currentTime.getHours();
        if (hours >= 10 && hours < 14) {
            dispatch({ type: 'SET_LOCK_TIMER', payload: '00:00:00' });
            return;
        }

        let targetTime = new Date(currentTime);
        if (hours >= 14) {
            targetTime.setDate(targetTime.getDate() + 1);
        }
        targetTime.setHours(10, 0, 0, 0);

        const diffSec = Math.floor((targetTime.getTime() - currentTime.getTime()) / 1000);
        dispatch({
            type: 'SET_LOCK_TIMER',
            payload: formatTime(Math.floor(diffSec / 3600), Math.floor((diffSec % 3600) / 60), diffSec % 60)
        });
    }, [formatTime]);

    // Таймер каждую секунду
    useEffect(() => {
        const timer = setInterval(() => {
            if (state.currentServerTime) {
                const newTime = new Date(state.currentServerTime.getTime() + 1000);
                dispatch({ type: 'SET_SERVER_TIME', payload: newTime });
                updateLockTimer(newTime);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [state.currentServerTime, updateLockTimer]);

    const convertEmptyToZero = (value: string, fieldType: string): string | number => {
        const cleanValue = value.replace(/\s+/g, '');
        if (cleanValue.includes('/')) return cleanValue;
        if (cleanValue === '000') return fieldType.toLowerCase() === 'float' ? 0.00 : 0;
        if (cleanValue.trim() === '') return fieldType.toLowerCase() === 'float' ? 0.00 : 0;
        return fieldType.toLowerCase() === 'float' ? parseFloat(cleanValue) : parseInt(cleanValue);
    };

    const isFieldEmpty = useCallback((value: string | number, fieldType: string): boolean => {
        if (value == null) return true;
        const strValue = String(value).trim();
        if (strValue === '') return true;
        if (strValue === '000') return false;
        if (fieldType.toLowerCase() === 'float' || fieldType.toLowerCase() === 'int') {
            const numValue = parseFloat(strValue);
            return isNaN(numValue) || numValue === 0;
        }
        return false;
    }, []);

    const formatValue = useCallback((value: string | number, type: string): string => {
        if (value == null || String(value).trim() === '') {
            return type.toLowerCase() === 'float' ? '0.00' : type.toLowerCase() === 'int' ? '0' : '';
        }
        if (type.toLowerCase() === 'float') {
            const num = parseFloat(String(value));
            return isNaN(num) ? '0.00' : num.toFixed(2);
        }
        if (type.toLowerCase() === 'int') {
            const num = parseInt(String(value));
            return isNaN(num) ? '0' : String(num);
        }
        return String(value);
    }, []);

    const safeEvaluate = useCallback((expression: string): number => {
        const sanitizedExpr = expression.replace(/\s+/g, '');
        if (!/^[0-9+\-*/().]+$/.test(sanitizedExpr)) throw new Error('Недопустимое выражение');
        // eslint-disable-next-line
        const result = new Function(`return ${sanitizedExpr}`)();
        if (typeof result !== 'number' || !isFinite(result)) throw new Error('Недопустимый результат');
        return result;
    }, []);

    const evaluateFormula = useCallback((formula: string, fieldType: string): string => {
        if (formula.startsWith('{') && formula.endsWith('}')) {
            const varName = formula.slice(1, -1);
            switch (varName) {
                case 'pharmacyNum': return state.pharmacyNum;
                case 'serverDate': return fieldType.toLowerCase() === 'datetime' ? state.serverDateISO : state.serverDate;
                case 'pastValueEndDay': return state.pastValue;
                default: return '';
            }
        }
        try {
            const fieldPattern = /(\w+)\((\d+)\)/g;
            let evaluatedFormula = formula.replace(fieldPattern, (_, fieldName) => formValues[fieldName] || '0');
            return safeEvaluate(evaluatedFormula).toString();
        } catch { return ''; }
    }, [formValues, state.pharmacyNum, state.serverDate, state.serverDateISO, state.pastValue, safeEvaluate]);

    const calculateFormulas = useCallback(() => {
        const newValues = { ...formValues };
        let hasChanges = false;
        tableInfo.forEach(field => {
            if (field.formula && field.InputBlock === 1) {
                const calculated = evaluateFormula(field.formula, field.cType);
                if (newValues[field.cName] !== calculated) {
                    newValues[field.cName] = calculated;
                    hasChanges = true;
                }
            }
        });
        if (hasChanges) setFormValues(newValues);
    }, [formValues, tableInfo, evaluateFormula]);

    const validateForm = useCallback(() => {
        if (state.isLocked) { setIsFormValid(false); return; }
        const required = tableInfo.filter(f => f.important === 1);
        const isValid = required.every(f => !isFieldEmpty(formValues[f.cName], f.cType));
        setIsFormValid(isValid);
    }, [formValues, state.isLocked, tableInfo, isFieldEmpty]);

    // === ВОССТАНОВЛЕННАЯ ЛОГИКА ДАТЫ - ОТДЕЛЬНЫЕ ВЫЗОВЫ API ===

    // Получение даты с сервера (КЛЮЧЕВОЙ ЭНДПОИНТ)
    const fetchServerDate = useCallback(async () => {
        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/current-date');
            const { date, isLocked } = response.data;
            // const date = '2025-12-11';
            // const isLocked = false;

            // Форматируем дату для отображения (DD.MM.YYYY)
            const formattedDate = new Date(date).toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });

            dispatch({
                type: 'SET_SERVER_DATE',
                payload: { date: formattedDate, dateISO: date }
            });
            dispatch({ type: 'SET_LOCKED', payload: isLocked });

            return date; // ISO дата для последующих запросов
        } catch (err) {
            console.error('Ошибка при получении даты с сервера:', err);
            notify.error('Ошибка при получении даты с сервера');
            return null;
        }
    }, []);

    // Получение времени с сервера
    const fetchServerTime = useCallback(async () => {
        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/current-time');
            const serverTime = new Date(response.data.time);
            dispatch({ type: 'SET_SERVER_TIME', payload: serverTime });
            updateLockTimer(serverTime);
        } catch (err) {
            console.error('Ошибка при получении времени с сервера:', err);
        }
    }, [updateLockTimer]);

    // Получение структуры таблицы
    const fetchTableFields = useCallback(async () => {
        const cachedFields = getCachedTableFields();
        if (cachedFields) {
            setTableInfo(cachedFields);
            return cachedFields;
        }

        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
            setTableInfo(response.data);
            setCachedTableFields(response.data);
            return response.data;
        } catch (err) {
            console.error('Ошибка при получении структуры таблицы:', err);
            notify.error('Ошибка при получении данных');
            return [];
        }
    }, []);

    // Получение номера аптеки
    const fetchPharmacyNum = useCallback(async () => {
        const username = localStorage.getItem('username');
        if (!username) return;

        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/user-info', {
                params: { username }
            });
            if (response.data.success) {
                dispatch({ type: 'SET_PHARMACY_NUM', payload: response.data.pharmacyNum });
            }
        } catch (err) {
            console.error('Ошибка при получении номера аптеки:', err);
        }
    }, []);

    // Получение данных пользователя за дату
    const fetchUserData = useCallback(async (serverDateISO: string, fields: TableInfo[]) => {
        const username = localStorage.getItem('username');
        if (!username || !serverDateISO) return;

        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/get-user-data', {
                params: {
                    username,
                    date: serverDateISO
                }
            });

            if (response.data.success && Object.keys(response.data.data).length > 0) {
                setFormValues(prev => ({
                    ...prev,
                    ...response.data.data
                }));
            } else {
                // Дефолтные значения если данных нет
                const defaultValues = fields.reduce((acc: { [key: string]: string }, field: TableInfo) => {
                    if (field.InputBlock !== 1) {
                        if (field.cType.toLowerCase() === 'int') {
                            acc[field.cName] = '0';
                        } else if (field.cType.toLowerCase() === 'float') {
                            acc[field.cName] = '0.00';
                        }
                    }
                    return acc;
                }, {});
                setFormValues(prev => ({ ...prev, ...defaultValues }));
            }
        } catch (err) {
            console.error('Ошибка при получении данных пользователя:', err);
        }
    }, []);

    // Получение прошлого значения
    const fetchPastValue = useCallback(async (serverDateISO: string) => {
        const username = localStorage.getItem('username');
        if (!username || !serverDateISO) return;

        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/past-value', {
                params: {
                    username,
                    date: serverDateISO
                }
            });

            if (response.data.success) {
                dispatch({ type: 'SET_PAST_VALUE', payload: response.data.value });
            } else {
                dispatch({ type: 'SET_PAST_VALUE', payload: '0' });
            }
        } catch (err) {
            console.error('Ошибка при получении прошлого значения:', err);
            dispatch({ type: 'SET_PAST_VALUE', payload: '0' });
        }
    }, []);

    // Инициализация - последовательные запросы как в старой версии
    useEffect(() => {
        const initDashboard = async () => {
            const username = localStorage.getItem('username');
            if (!username) { navigate('/login'); return; }

            try {
                // 1. Сначала получаем дату - ЭТО САМОЕ ВАЖНОЕ
                const serverDateISO = await fetchServerDate();
                if (!serverDateISO) {
                    dispatch({ type: 'SET_LOADING', payload: false });
                    return;
                }

                // 2. Получаем структуру таблицы
                const fields = await fetchTableFields();

                // 3. Получаем номер аптеки
                await fetchPharmacyNum();

                // 4. Получаем время с сервера
                await fetchServerTime();

                // 5. Небольшая задержка перед получением данных пользователя (как в старой версии)
                setTimeout(async () => {
                    await fetchUserData(serverDateISO, fields);
                    await fetchPastValue(serverDateISO);

                    dispatch({ type: 'SET_INITIALIZED', payload: true });
                    dispatch({ type: 'SET_LOADING', payload: false });
                }, 500);

            } catch (error) {
                console.error('Ошибка инициализации:', error);
                notify.error('Ошибка загрузки данных');
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        initDashboard();
    }, [navigate, fetchServerDate, fetchTableFields, fetchPharmacyNum, fetchServerTime, fetchUserData, fetchPastValue]);

    // Синхронизация времени с сервером каждую минуту
    useEffect(() => {
        const serverSync = setInterval(fetchServerTime, 60000);
        return () => clearInterval(serverSync);
    }, [fetchServerTime]);

    useEffect(() => {
        validateForm();
        calculateFormulas();
    }, [formValues, tableInfo, state.serverDate, state.pharmacyNum, state.isLocked, validateForm, calculateFormulas]);

    const formatNumberWithSpaces = useCallback((value: any, fieldType: string): string => {
        const stringValue = String(value || '');
        if (stringValue === '' || stringValue === '0') return stringValue;
        const cleanValue = stringValue.replace(/\s+/g, '');
        const [integerPart, decimalPart] = cleanValue.split('.');
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        if (fieldType.toLowerCase() === 'float') {
            return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
        }
        return formattedInteger;
    }, []);

    const handleInputChange = useCallback((fieldName: string, value: string, fieldType: string) => {
        if (state.isLocked) return;
        const field = tableInfo.find(f => f.cName === fieldName);
        if (!field || field.InputBlock === 1) return;

        let processedValue = value.replace(/\s+/g, '');
        if (fieldType.toLowerCase() === 'float') {
            processedValue = processedValue.replace(',', '.').replace(/[^\d.-]/g, '');
            if (processedValue === '' || /^-?\d*\.?\d{0,2}$/.test(processedValue)) {
                setFormValues(prev => ({ ...prev, [fieldName]: processedValue }));
            }
        } else if (fieldType.toLowerCase() === 'int') {
            processedValue = processedValue.replace(/[^\d-]/g, '').replace(/(-)?-+/g, '$1');
            if (processedValue === '' || /^-?\d*$/.test(processedValue)) {
                setFormValues(prev => ({ ...prev, [fieldName]: processedValue }));
            }
        } else {
            setFormValues(prev => ({ ...prev, [fieldName]: value }));
        }
    }, [state.isLocked, tableInfo]);

    const handleBlur = useCallback((fieldName: string, fieldType: string) => {
        const currentValue = formValues[fieldName];
        if (currentValue == null || String(currentValue).trim() === '') {
            setFormValues(prev => ({ ...prev, [fieldName]: formatValue('', fieldType) }));
            return;
        }
        if (String(currentValue).trim() === '000') return;

        if (fieldType.toLowerCase() === 'float') {
            const num = parseFloat(String(currentValue).replace(/\s+/g, ''));
            setFormValues(prev => ({
                ...prev,
                [fieldName]: !isNaN(num) ? formatNumberWithSpaces(num.toFixed(2), fieldType) : '0.00'
            }));
        } else if (fieldType.toLowerCase() === 'int') {
            const num = parseInt(String(currentValue).replace(/\s+/g, ''));
            setFormValues(prev => ({
                ...prev,
                [fieldName]: !isNaN(num) ? formatNumberWithSpaces(String(num), fieldType) : '0'
            }));
        }
    }, [formValues, formatNumberWithSpaces, formatValue]);

    const formatDateToISO = (dateStr: string): string => {
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month}-${day}`;
    };

    const handleSave = useCallback(async () => {
        if (!isFormValid || state.isLocked) return;
        const username = localStorage.getItem('username');
        const formattedDate = formatDateToISO(state.serverDate);

        const processedFormValues = Object.entries(formValues).reduce((acc, [key, value]) => {
            const field = tableInfo.find(f => f.cName === key);
            acc[key] = field ? convertEmptyToZero(String(value), field.cType) : value;
            return acc;
        }, {} as Record<string, string | number>);

        try {
            const response = await axios.post('https://tbl.zerdegroup.kz/api/save-data', {
                username,
                formData: { ...processedFormValues, date: formattedDate }
            });
            if (response.data.success) {
                notify.success('Данные успешно сохранены');
            }
        } catch {
            notify.error('Ошибка при сохранении данных');
        }
    }, [isFormValid, state.isLocked, state.serverDate, formValues, tableInfo]);

    if (state.loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#18181b', letterSpacing: '-0.02em' }}>
                    Отчет за <span style={{ color: '#ef4444' }}>{state.serverDate}</span>
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ color: '#71717a' }}>
                        Блокировка: 10:00 – 14:00
                    </Typography>
                    {!state.isLocked && (
                        <Typography variant="body2" sx={{ color: '#09090b', fontWeight: 500 }}>
                            До блокировки: {state.lockTimer}
                        </Typography>
                    )}
                    {state.isLocked && (
                        <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600 }}>
                            Форма заблокирована
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Группы полей */}
            {Object.entries(groupedFields).map(([groupName, fields]) => (
                <Box key={groupName} sx={{ mb: 5 }}>
                    <Typography
                        sx={{
                            color: '#09090b',
                            fontWeight: 600,
                            fontSize: '1rem',
                            mb: 2,
                            pl: 0.5,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        {groupName}
                    </Typography>

                    <Box sx={{
                        border: '1px solid #e4e4e7', // zinc-200
                        borderRadius: '12px',
                        overflow: 'hidden',
                        bgcolor: 'white'
                    }}>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableBody>
                                {fields.map((field, index) => {
                                    const isMandatory = field.important === 1;
                                    const isEmpty = isMandatory && isFieldEmpty(formValues[field.cName] || '', field.cType);

                                    return (
                                        <TableRow
                                            key={field.id}
                                            sx={{
                                                bgcolor: 'white',
                                                '&:not(:last-child) td': {
                                                    borderBottom: '1px solid #f4f4f5' // zinc-100
                                                }
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    width: '50%',
                                                    py: 1.75,
                                                    pl: 3,
                                                    fontSize: '0.9rem',
                                                    fontWeight: isMandatory ? 500 : 400,
                                                    color: isMandatory ? '#09090b' : '#52525b',
                                                    borderBottom: 'none',
                                                    borderRight: '1px solid #f4f4f5'
                                                }}
                                            >
                                                {field.displayName}
                                                {isMandatory && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                                            </TableCell>
                                            <TableCell sx={{ py: 1, px: 2, borderBottom: 'none' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="standard"
                                                    value={formValues[field.cName] !== undefined
                                                        ? formatNumberWithSpaces(formValues[field.cName], field.cType)
                                                        : ''}
                                                    onChange={(e) => handleInputChange(
                                                        field.cName,
                                                        e.target.value.replace(/\s+/g, ''),
                                                        field.cType
                                                    )}
                                                    onBlur={() => handleBlur(field.cName, field.cType)}
                                                    disabled={state.isLocked || field.InputBlock === 1}

                                                    InputProps={{
                                                        disableUnderline: true,
                                                        sx: {
                                                            fontSize: '0.9rem',
                                                            fontWeight: 500,
                                                            fontFamily: '"Geist Mono", "Roboto Mono", monospace',
                                                            color: field.InputBlock === 1 ? '#a1a1aa' : '#09090b',
                                                            bgcolor: field.InputBlock === 1
                                                                ? 'transparent'
                                                                : (isMandatory && isEmpty ? '#fef2f2' : '#f4f4f5'), // Red tint if mandatory empty, else zinc
                                                            border: isMandatory && isEmpty ? '1px solid #fca5a5' : '1px solid transparent', // Red border if mandatory empty
                                                            borderRadius: '6px',
                                                            px: 1.5,
                                                            py: 0.75,
                                                            transition: 'all 0.2s',
                                                            textAlign: 'right',
                                                            '&:hover': {
                                                                bgcolor: field.InputBlock === 1 ? 'transparent' : (isMandatory && isEmpty ? '#fee2e2' : '#e4e4e7'),
                                                            },
                                                            '&.Mui-focused': {
                                                                bgcolor: 'white',
                                                                boxShadow: isMandatory ? '0 0 0 1px #ef4444' : '0 0 0 1px #09090b',
                                                                borderColor: 'transparent'
                                                            }
                                                        }
                                                    }}
                                                    inputProps={{
                                                        style: { textAlign: 'right', padding: 0 }
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            ))}

            {/* Кнопка сохранения */}
            <Box sx={{ mt: 4, textAlign: 'right', width: '100%', pb: 8 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isFormValid || state.isLocked}
                    disableElevation
                    sx={{
                        bgcolor: '#18181b', // zinc-900
                        color: 'white',
                        py: 1.5,
                        px: 6,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                            bgcolor: '#27272a',
                        },
                        '&:disabled': {
                            bgcolor: '#e4e4e7',
                            color: '#a1a1aa'
                        }
                    }}
                >
                    Сохранить отчет
                </Button>
            </Box>
        </Box>
    );
};

export default CurrentDashboard;